import React, { useState } from 'react';
import { Backdrop, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { FindContractStep } from './FindContractStep';
import { ExplanationForm, ExplanationStep } from './ExplanationStep';
import { ContactDetailsStep } from './ContactDetailsStep';
import { useNavigate } from 'react-router-dom';
import { Contract } from 'modules/contract/types';
import { ClientContactInformation } from 'modules/service-call/types';
import { ServiceCallsApi } from 'modules/service-call/api';
import { useIntl } from 'react-intl';
import { CreateProductStep, NewProduct } from './CreateProductStep';
import { ChooseLineItemStep } from './ChooseLineItemStep';


export const CreateNewServiceCallSection: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contract, setContract] = useState<Contract>();
  const [lineItemIndex, setLineItemIndex] = useState<number>();
  const [newProduct, setNewProduct] = useState<NewProduct>();
  const [explanationForm, setExplanationForm] = useState<ExplanationForm>();

  const { formatMessage } = useIntl();

  const onStepcomplete = () => setActiveStep((x) => x + 1);

  const onFindContractStepComplete = (selectedContract: Contract) => {
    setContract(selectedContract);
    onStepcomplete();
  };

  const onChooseLineItemStepComplete = (lineItemId: number) => {
    setLineItemIndex(lineItemId);
    onStepcomplete();
  };

  const onCreateProductStepComplete = (newProduct: NewProduct) => {
    setNewProduct(newProduct);
    onStepcomplete();
  };

  const onExplanationStepComplete = (explanationForm: ExplanationForm) => {
    setExplanationForm(explanationForm);

    onStepcomplete();
  };

  const onContactDetailsStepComplete = (clientContactInformation: ClientContactInformation) => {
    if (contract && explanationForm) {
      setIsSubmitting(true);
      ServiceCallsApi.create(
        {
          contractNumber: contract.id,
          clientNumber: contract.clientNumber,
          productId: lineItemIndex ? contract.lineItems[lineItemIndex].product.id : undefined,
          productName: newProduct?.name,
          productSku: newProduct?.sku,
          reasonId: explanationForm?.reasonId,
          note: explanationForm.note,
          contactInformation: clientContactInformation,
        },
        explanationForm.attachments
      ).then(() => {
        setIsSubmitting(false);
        navigate('/submission-confirmation', {
          state: {
            email: clientContactInformation.email,
          }
        });
        onStepcomplete();
      });
    }
  };

  const getState = (stepId: number) => {
    if (stepId < activeStep) return 'complete';
    else if (stepId === activeStep) return 'active';
    else return 'incomplete';
  };

  const resetFindContractStep = () => {
    setContract(undefined);
  };
  const resetChooseArticleStep = () => {
    setLineItemIndex(undefined);
  };
  const resetCreateArticleStep = () => {
    setNewProduct(undefined);
  };
  const resetExplenationStep = () => {
    setExplanationForm(undefined);
  };

  const resetToFindContractStep = () => {
    resetFindContractStep();
    resetChooseArticleStep();
    resetExplenationStep();

    setActiveStep(0);
  };
  const resetToProductStep = () => {
    resetChooseArticleStep();
    resetCreateArticleStep();
    resetExplenationStep();

    setActiveStep(1);
  };
  const resetToExplanationStep = () => {
    setActiveStep(2);
  };

  return (
    <Stack gap={4}>
      <Backdrop open={isSubmitting} sx={{ color: 'common.white', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Stack alignItems='center' gap={3}>
          <CircularProgress color='inherit' size={90} />
          <Typography color='inherit' variant='h4'>{formatMessage({ id: 'CreateNewServiceCallSection.Submitting', defaultMessage: 'Your request is being processed' })}</Typography>
        </Stack>
      </Backdrop >

      <FindContractStep state={getState(0)} completeStep={onFindContractStepComplete} onEditStep={resetToFindContractStep}
        contract={contract}
      />
      <Divider light />
      {contract?.lineItems.length === 0 ?
        <CreateProductStep state={getState(1)} completeStep={onCreateProductStepComplete} onEditStep={resetToProductStep}
          newProduct={newProduct}
        /> :
        <ChooseLineItemStep state={getState(1)} completeStep={onChooseLineItemStepComplete} onEditStep={resetToProductStep}
          contract={contract}
          lineItemIndex={lineItemIndex}
        />
      }
      <Divider light />
      <ExplanationStep state={getState(2)} completeStep={onExplanationStepComplete} onEditStep={resetToExplanationStep}
        explanationFrom={explanationForm}
      />
      <Divider light />
      <ContactDetailsStep state={getState(3)} completeStep={onContactDetailsStepComplete} />
      <Divider light />
    </Stack >
  );
};